import { useEffect, useState } from 'react';
import { fetchActivePlacements, recordImpression, recordClick, type AdPlacement } from '../../features/ads/api';

interface AdBannerProps {
  topicId: string;
  type?: 'banner' | 'inline' | 'spotlight';
}

export function AdBanner({ topicId, type = 'banner' }: AdBannerProps) {
  const [placement, setPlacement] = useState<AdPlacement | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAd();
  }, [topicId, type]);

  async function loadAd() {
    try {
      setLoading(true);
      const placements = await fetchActivePlacements(topicId, 1);

      if (placements && placements.length > 0) {
        const ad = placements[0];
        setPlacement(ad);

        // Record impression
        try {
          await recordImpression(ad.id);
        } catch (err) {
          console.warn('Failed to record impression:', err);
        }
      }
    } catch (err) {
      console.error('Failed to load ad:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleClick() {
    if (!placement) return;

    // Record click
    try {
      await recordClick(placement.id);
    } catch (err) {
      console.warn('Failed to record click:', err);
    }

    // Open target URL
    if (placement.targetUrl) {
      window.open(placement.targetUrl, '_blank', 'noopener,noreferrer');
    }
  }

  if (loading || !placement) {
    return null;
  }

  // Banner style (top of page)
  if (type === 'banner') {
    return (
      <div className="mb-6">
        <div className="text-xs text-slate-500 mb-1">Sponsored</div>
        <div
          onClick={handleClick}
          className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-slate-700 rounded-lg p-6 cursor-pointer hover:border-slate-600 transition-colors"
        >
          {placement.creativeUrl ? (
            <img
              src={placement.creativeUrl}
              alt="Advertisement"
              className="w-full h-32 object-cover rounded"
            />
          ) : (
            <div className="text-center">
              <div className="text-lg font-semibold text-slate-100 mb-2">
                Featured Advertisement
              </div>
              <div className="text-sm text-slate-400">
                Click to learn more
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Inline style (between content)
  if (type === 'inline') {
    return (
      <div className="my-4">
        <div className="text-xs text-slate-500 mb-1">Sponsored</div>
        <div
          onClick={handleClick}
          className="bg-slate-800 border border-slate-700 rounded-lg p-4 cursor-pointer hover:border-slate-600 transition-colors"
        >
          <div className="flex items-center gap-4">
            {placement.creativeUrl && (
              <img
                src={placement.creativeUrl}
                alt="Advertisement"
                className="w-24 h-24 object-cover rounded"
              />
            )}
            <div className="flex-1">
              <div className="text-sm font-medium text-slate-100 mb-1">
                Sponsored Content
              </div>
              <div className="text-xs text-slate-400">
                Click to learn more →
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Spotlight style (sidebar or prominent)
  return (
    <div className="mb-4">
      <div className="text-xs text-slate-500 mb-1">Featured</div>
      <div
        onClick={handleClick}
        className="bg-gradient-to-br from-yellow-900/20 to-orange-900/20 border border-yellow-800/50 rounded-lg p-4 cursor-pointer hover:border-yellow-700 transition-colors"
      >
        {placement.creativeUrl ? (
          <img
            src={placement.creativeUrl}
            alt="Featured Advertisement"
            className="w-full h-48 object-cover rounded mb-3"
          />
        ) : (
          <div className="text-center py-8">
            <div className="text-xl font-bold text-yellow-400 mb-2">⭐</div>
            <div className="text-sm font-semibold text-slate-100">
              Featured Spotlight
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
